/**
 * VERA Case Page
 * Displays evidence items for a specific case with organized thumbnails and metadata
 */
import React, { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  makeStyles,
  tokens,
  Text,
  Card,
  Badge,
  Button,
  Input,
  Dropdown,
  Option,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbButton,
  BreadcrumbDivider,
  Divider,
  Tooltip,
} from "@fluentui/react-components";
import {
  Cube24Regular,
  Cube24Filled,
  Image24Regular,
  Video24Regular,
  DocumentBulletList24Regular,
  ArrowDownload24Regular,
  Eye24Regular,
  Search24Regular,
  Filter24Regular,
  Info24Regular,
  Clock24Regular,
  Person24Regular,
  Location24Regular,
  bundleIcon,
} from "@fluentui/react-icons";

import { getCaseById, getEvidenceForCase } from "../data/mockData";
import { EvidenceThumbnail } from "../components/EvidenceThumbnail";
import type { EvidenceItem, EvidenceCategory, CaseStatus } from "../types";

const CubeIcon = bundleIcon(Cube24Filled, Cube24Regular);

const categoryIcons: Record<EvidenceCategory, React.ReactNode> = {
  "3d-scan": <CubeIcon />,
  photograph: <Image24Regular />,
  video: <Video24Regular />,
  document: <DocumentBulletList24Regular />,
  audio: <DocumentBulletList24Regular />,
};

const categoryLabels: Record<EvidenceCategory, string> = {
  "3d-scan": "3D Scan",
  photograph: "Photograph",
  video: "Video",
  document: "Document",
  audio: "Audio",
};

const statusConfig: Record<
  CaseStatus,
  { label: string; color: "important" | "warning" | "success" | "informative" }
> = {
  active: { label: "Active", color: "important" },
  "under-review": { label: "Under Review", color: "warning" },
  closed: { label: "Closed", color: "success" },
  archived: { label: "Archived", color: "informative" },
};

const useStyles = makeStyles({
  container: {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: tokens.spacingHorizontalL,
  },
  breadcrumbs: {
    marginBottom: tokens.spacingVerticalL,
  },
  caseHeader: {
    marginBottom: "24px",
    paddingBottom: "20px",
    borderBottom: "1px solid #e2e8f0",
  },
  caseHeaderTop: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginBottom: "12px",
  },
  caseNumber: {
    fontSize: "14px",
    fontWeight: 600,
    color: "#004C97",
    fontFamily: "'Poppins', sans-serif",
    letterSpacing: "0.5px",
  },
  caseTitle: {
    fontFamily: "'Poppins', sans-serif",
    color: "#2D2926",
    marginTop: "4px",
  },
  caseDescription: {
    fontSize: "14px",
    color: "#63666A",
    lineHeight: "1.6",
    marginTop: "8px",
    maxWidth: "800px",
  },
  caseMeta: {
    display: "flex",
    flexWrap: "wrap",
    gap: "24px",
    marginTop: "16px",
  },
  metaItem: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "13px",
    color: "#63666A",
  },
  metaIcon: {
    color: "#004C97",
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "20px",
    flexWrap: "wrap",
    gap: "12px",
  },
  toolbarLeft: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  toolbarRight: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  filterChip: {
    cursor: "pointer",
    fontSize: "12px",
    fontWeight: 500,
    padding: "6px 14px",
    borderRadius: "20px",
    border: "1px solid #e2e8f0",
    backgroundColor: "#ffffff",
    color: "#63666A",
    transition: "all 0.15s ease",
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  filterChipActive: {
    backgroundColor: "#004C97",
    color: "#ffffff",
    border: "1px solid #004C97",
  },
  evidenceGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
    gap: "20px",
  },
  evidenceCard: {
    cursor: "pointer",
    transition: "transform 0.2s, box-shadow 0.2s",
    border: "1px solid #e2e8f0",
    borderRadius: "12px",
    overflow: "hidden",
    ":hover": {
      transform: "translateY(-4px)",
      boxShadow: "0 12px 24px rgba(0, 0, 0, 0.1)",
    },
  },
  thumbnailArea: {
    height: "160px",
    overflow: "hidden",
    position: "relative" as const,
  },
  formatBadge: {
    position: "absolute" as const,
    top: "12px",
    right: "12px",
  },
  categoryBadge: {
    position: "absolute" as const,
    top: "12px",
    left: "12px",
  },
  cardContent: {
    padding: "16px",
  },
  itemNumber: {
    fontSize: "11px",
    fontWeight: 600,
    color: "#004C97",
    fontFamily: "'Poppins', sans-serif",
    letterSpacing: "0.5px",
    marginBottom: "4px",
  },
  evidenceName: {
    fontSize: "14px",
    fontWeight: 600,
    color: "#2D2926",
    lineHeight: "1.3",
  },
  evidenceDescription: {
    fontSize: "12px",
    color: "#63666A",
    marginTop: "6px",
    lineHeight: "1.4",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },
  cardMeta: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: "12px",
    paddingTop: "12px",
    borderTop: "1px solid #f1f3f4",
  },
  fileSize: {
    fontSize: "11px",
    color: "#8a8d91",
  },
  cardActions: {
    display: "flex",
    gap: "4px",
  },
  emptyState: {
    textAlign: "center" as const,
    padding: "48px 20px",
    color: "#63666A",
  },
  notFound: {
    textAlign: "center" as const,
    padding: "80px 20px",
  },
});

const formatFileSize = (mb: number): string => {
  if (mb >= 1024) return `${(mb / 1024).toFixed(1)} GB`;
  return `${mb.toFixed(1)} MB`;
};

export const CasePage: React.FC = () => {
  const styles = useStyles();
  const { caseId } = useParams<{ caseId: string }>();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState<EvidenceCategory | "all">(
    "all"
  );

  const caseData = caseId ? getCaseById(caseId) : undefined;
  const evidence = caseId ? getEvidenceForCase(caseId) : [];

  if (!caseData) {
    return (
      <div className={styles.container}>
        <div className={styles.notFound}>
          <Text size={700} weight="semibold" block>
            Case Not Found
          </Text>
          <Text
            size={400}
            block
            style={{ marginTop: "8px", color: "#63666A" }}
          >
            The requested case could not be located.
          </Text>
          <Button
            appearance="primary"
            onClick={() => navigate("/")}
            style={{ marginTop: "20px" }}
          >
            Return to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  const config = statusConfig[caseData.status];

  // Filter evidence
  const filteredEvidence = evidence.filter((item) => {
    const matchesSearch =
      !searchQuery ||
      item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.itemNumber.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesFilter =
      activeFilter === "all" || item.category === activeFilter;

    return matchesSearch && matchesFilter;
  });

  // Get unique categories in this case's evidence
  const availableCategories = Array.from(
    new Set(evidence.map((e) => e.category))
  );

  const handleEvidenceClick = (item: EvidenceItem) => {
    navigate(`/case/${caseId}/evidence/${item.id}`);
  };

  const handleDownload = (e: React.MouseEvent, item: EvidenceItem) => {
    e.stopPropagation();
    // In production, this would initiate a download from Azure Blob Storage
    alert(
      `Download initiated for: ${item.name}\nFile: ${item.fileUrl}\nSize: ${formatFileSize(item.fileSizeMB)}`
    );
  };

  return (
    <div className={styles.container}>
      {/* Breadcrumbs */}
      <Breadcrumb className={styles.breadcrumbs}>
        <BreadcrumbItem>
          <BreadcrumbButton onClick={() => navigate("/")}>
            Dashboard
          </BreadcrumbButton>
        </BreadcrumbItem>
        <BreadcrumbDivider />
        <BreadcrumbItem>
          <BreadcrumbButton current>{caseData.caseNumber}</BreadcrumbButton>
        </BreadcrumbItem>
      </Breadcrumb>

      {/* Case Header */}
      <div className={styles.caseHeader}>
        <div className={styles.caseHeaderTop}>
          <div>
            <Text className={styles.caseNumber}>{caseData.caseNumber}</Text>
            <Text size={700} weight="semibold" className={styles.caseTitle} block>
              {caseData.title}
            </Text>
          </div>
          <Badge size="large" appearance="filled" color={config.color}>
            {config.label}
          </Badge>
        </div>

        <Text className={styles.caseDescription}>{caseData.description}</Text>

        <div className={styles.caseMeta}>
          <div className={styles.metaItem}>
            <Person24Regular className={styles.metaIcon} />
            {caseData.assignedOfficer}
          </div>
          <div className={styles.metaItem}>
            <Info24Regular className={styles.metaIcon} />
            {caseData.assignedUnit}
          </div>
          <div className={styles.metaItem}>
            <Clock24Regular className={styles.metaIcon} />
            Incident: {new Date(caseData.incidentDate).toLocaleDateString()}
          </div>
          <div className={styles.metaItem}>
            <Location24Regular className={styles.metaIcon} />
            {caseData.incidentLocation}
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className={styles.toolbar}>
        <div className={styles.toolbarLeft}>
          <Input
            contentBefore={<Search24Regular />}
            placeholder="Search evidence..."
            value={searchQuery}
            onChange={(_, data) => setSearchQuery(data.value)}
            style={{ minWidth: "260px" }}
          />

          {/* Category Filters */}
          <div
            className={`${styles.filterChip} ${
              activeFilter === "all" ? styles.filterChipActive : ""
            }`}
            onClick={() => setActiveFilter("all")}
            role="button"
            tabIndex={0}
          >
            All ({evidence.length})
          </div>
          {availableCategories.map((cat) => {
            const count = evidence.filter((e) => e.category === cat).length;
            return (
              <div
                key={cat}
                className={`${styles.filterChip} ${
                  activeFilter === cat ? styles.filterChipActive : ""
                }`}
                onClick={() => setActiveFilter(cat)}
                role="button"
                tabIndex={0}
              >
                {categoryIcons[cat]}
                {categoryLabels[cat]} ({count})
              </div>
            );
          })}
        </div>

        <div className={styles.toolbarRight}>
          <Text size={200} style={{ color: "#63666A" }}>
            {filteredEvidence.length} item
            {filteredEvidence.length !== 1 ? "s" : ""}
          </Text>
        </div>
      </div>

      {/* Evidence Grid */}
      {filteredEvidence.length === 0 ? (
        <div className={styles.emptyState}>
          <Text size={500} weight="semibold" block>
            No evidence found
          </Text>
          <Text
            size={400}
            block
            style={{ marginTop: "8px" }}
          >
            {searchQuery
              ? "Try adjusting your search or filter criteria."
              : "No evidence items have been added to this case yet."}
          </Text>
        </div>
      ) : (
        <div className={styles.evidenceGrid}>
          {filteredEvidence.map((item) => (
            <Card
              key={item.id}
              className={styles.evidenceCard}
              onClick={() => handleEvidenceClick(item)}
            >
              {/* Thumbnail Area */}
              <div
                className={styles.thumbnailArea}
                style={{ position: "relative" }}
              >
                <EvidenceThumbnail
                  category={item.category}
                  format={item.format}
                  fileUrl={item.fileUrl}
                  hasRealFile={item.hasRealFile}
                  height={160}
                />

                {/* Format badge */}
                <Badge
                  className={styles.formatBadge}
                  size="small"
                  appearance="filled"
                  color="informative"
                >
                  .{item.format.toUpperCase()}
                </Badge>

                {/* Category badge */}
                <Badge
                  className={styles.categoryBadge}
                  size="small"
                  appearance="tint"
                >
                  {categoryLabels[item.category]}
                </Badge>
              </div>

              {/* Card Content */}
              <div className={styles.cardContent}>
                <Text className={styles.itemNumber}>
                  Item #{item.itemNumber}
                </Text>
                <Text className={styles.evidenceName} block>
                  {item.name}
                </Text>
                <Text className={styles.evidenceDescription}>
                  {item.description}
                </Text>

                {/* Metadata */}
                <div className={styles.cardMeta}>
                  <Text className={styles.fileSize}>
                    {formatFileSize(item.fileSizeMB)}
                    {item.vertexCount
                      ? ` • ${(item.vertexCount / 1000000).toFixed(1)}M vertices`
                      : ""}
                  </Text>
                  <div className={styles.cardActions}>
                    <Tooltip content="View evidence" relationship="label">
                      <Button
                        appearance="subtle"
                        size="small"
                        icon={<Eye24Regular />}
                      />
                    </Tooltip>
                    <Tooltip content="Download" relationship="label">
                      <Button
                        appearance="subtle"
                        size="small"
                        icon={<ArrowDownload24Regular />}
                        onClick={(e) => handleDownload(e, item)}
                      />
                    </Tooltip>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default CasePage;
